import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { isAuthenticated, addSchoolFee } from "../../../ApiService/ApiService";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAcademicSessions,
  selectAllSessions,
  selectSessionsLoading,
  selectSessionsError,
} from "../../../Slices/sessionsSlice";
import {
  fetchFeeTypesAsync,
  selectAllFeeTypes,
  selectFeeTypesLoading,
  selectFeeTypesError,
} from "../../../Slices/FeeTypeSlice";
import {
  fetchGradesAsync,
  selectAllGrades,
  selectGradesLoading,
  selectGradesError,
} from "../../../Slices/gradesSlice";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import CustomDialog from "../../../Components/CustomDialog";

function AddFee({ setShowAddFees, fetchSchoolFeesDetails }) {
  const schoolId = sessionStorage.getItem("schoolId");
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchAcademicSessions());
    dispatch(fetchFeeTypesAsync());
    dispatch(fetchGradesAsync());
  }, [dispatch]);

  const sessions = useSelector(selectAllSessions);
  const sessionsLoading = useSelector(selectSessionsLoading);
  const sessionsError = useSelector(selectSessionsError);

  const feeTypes = useSelector(selectAllFeeTypes);
  const feeTypesLoading = useSelector(selectFeeTypesLoading);
  const feeTypesError = useSelector(selectFeeTypesError);

  const grades = useSelector(selectAllGrades);
  const gradesLoading = useSelector(selectGradesLoading);
  const gradesError = useSelector(selectGradesError);
  const [formData, setFormData] = useState({
    feeTypeId: "",
    schoolId: schoolId,
    gradeId: "",
    sessionId: "",
    amount: 0,
  });

  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "feeTypeId" && value === 1) {
      setFormData({
        ...formData,
        [name]: value,
        gradeId: null,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const response = await addSchoolFee(formData);
        if (response && response?.data?.status === 200) {
          setDialog({
            visible: true,
            title: "Success",
            message: "Fees added",
            buttons: [{ name: "OK", onPress: handleDialogClose }],
          });
          setFormData({
            feeTypeId: "",
            schoolId: schoolId,
            gradeId: "",
            sessionId: "",
            amount: 0,
          });
          setShowAddFees(false);
          fetchSchoolFeesDetails(schoolId);
        } else {
          const errorMessage =
            response?.data?.errorMessage || "Something went wrong";
          setDialog({
            visible: true,
            title: "Error",
            message: errorMessage,
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
         setDialog({
           visible: true,
           title: "Unauthorized action",
           message: "Please login and try again",
           buttons: [{ name: "Close", onPress: handleDialogClose }],
         });
      }
    } catch (error) {
      console.error("Failed to add fee", error);
       setDialog({
         visible: true,
         title: "Error",
         message: "Failed to add fee",
         buttons: [{ name: "Close", onPress: handleDialogClose }],
       });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box sx={{ maxWidth: "75%", margin: "auto", padding: 3 }}>
        <Paper elevation={3} sx={{ p: 5 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: 22, color: "black" }}
          >
            Add Fee
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="feeTypes">Fee Types</InputLabel>
                  <Select
                    name="feeTypeId"
                    label="Fee Types"
                    labelId="feeTypes"
                    value={formData.feeTypeId}
                    onChange={handleChange}
                    size="small"
                    disabled={feeTypesLoading || feeTypesError}
                  >
                    {feeTypesLoading ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : feeTypesError ? (
                      <MenuItem value="">Error fetching classes</MenuItem>
                    ) : (
                      feeTypes?.map((currFeeType) => (
                        <MenuItem key={currFeeType.id} value={currFeeType.id}>
                          {currFeeType.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <FormControl fullWidth>
                  <InputLabel id="grade" size="small">
                    Grade *
                  </InputLabel>
                  <Select
                    id="gradeId"
                    name="gradeId"
                    labelId="grade"
                    value={formData.feeTypeId === 1 ? "All" : formData.gradeId}
                    onChange={handleChange}
                    label="Grade"
                    size="small"
                    required
                    disabled={
                      gradesError || gradesLoading || formData.feeTypeId === 1
                    }
                  >
                    {gradesLoading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : gradesError ? (
                      <MenuItem disabled>Error: Failed to load grades</MenuItem>
                    ) : (
                      [
                        formData.feeTypeId === 1 && (
                          <MenuItem key="all" value="All">
                            All
                          </MenuItem>
                        ),
                        ...grades.map((grade) => (
                          <MenuItem key={grade.id} value={grade.id}>
                            {grade.name}
                          </MenuItem>
                        )),
                      ]
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="sessions">Sessions</InputLabel>
                  <Select
                    name="sessionId"
                    label="Sessions"
                    labelId="sessions"
                    value={formData.sessionId}
                    onChange={handleChange}
                    size="small"
                    disabled={sessionsLoading || sessionsError}
                  >
                    {sessionsLoading ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : sessionsError ? (
                      <MenuItem value="">Error fetching classes</MenuItem>
                    ) : (
                      sessions?.data?.map((currSession) => (
                        <MenuItem key={currSession.id} value={currSession.id}>
                          {currSession.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  size="small"
                  required
                  inputProps={{
                    pattern: "[0-9]+([.][0-9]{1,2})?",
                    min: 0,
                    step: "any",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => setShowAddFees(false)}
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: "unset" }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={<ArrowForward />}
                  sx={{ textTransform: "unset" }}
                >
                  {loading ? "Loading..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default AddFee;
